import { ReactElement, useCallback } from "react";
import { IFormHandler } from "client/Forms";
import { localize } from "client_legacy/ndaw/culture";
import {
	DatePicker,
	Form,
	FormItem,
	InputWithMoreInfo,
	Select,
	SelectItem,
	TextArea,
	TextInput,
	TimeZonePicker,
	Toggle,
	useAlertDialog,
	ValidationError
} from "client/Components";
import {
	UserProfileEditRestrictions,
	UserPronounKind
} from "client/Api/UserProfile/service";
import { TagKind } from "client/Api/Tags/tags";
import { WorkingDaysInput } from "./WorkingDaysInput";
import { UserTagSelect } from "./UserTagSelect";
import { TimeZone } from "client/DateAndTime/timezones";
import styles from "./UserProfileForm.module.less";
import { CustomStringFormData, UserProfileFormData } from "./UserProfileEdit";
import { IValueInputAttachable } from "client/Forms/attachable";

export type UserProfileFormProps = {
	formHandler: IFormHandler<UserProfileFormData>;
	editRestrictions: UserProfileEditRestrictions | undefined;
	emailAddress: string | undefined;
	saveHandler: () => void;
	birthdayTimezone: TimeZone;
};

export const UserProfileForm = (props: UserProfileFormProps): ReactElement => {
	const {
		formHandler,
		editRestrictions,
		emailAddress,
		saveHandler,
		birthdayTimezone
	} = props;

	const [alertDialog, openAlertDialog] = useAlertDialog();

	const birthdayMoreInfoHandler = useCallback(
		(): void => {
			openAlertDialog({
				title: localize("_BirthYear"),
				children: localize("_YourBirthYearWontBeDisplayedInYourProfileOrInTheBirthdaysTileOnYourWorkspace"),
				showCloseButton: true,
				trackingId: "birth-year-info-dialog"
			});
		},
		[openAlertDialog]
	);

	return (
		<>
			<Form
				onSubmit={saveHandler}
				hasWidthConstrained
			>
				<h3>{localize("_PersonalInformation")}</h3>
				<FormItem
					label={localize("_FirstName")}
					isRequired
				>
					<TextInput
						{...formHandler.for("firstName")
							.attachToTextInput()}
						isDisabled={editRestrictions?.firstName.isDisabled}
					/>
					<ValidationError
						{...formHandler.for("firstName")
							.attachToValidationError()}
					/>
				</FormItem>

				<FormItem
					label={localize("_LastName")}
					isRequired
				>
					<TextInput
						{...formHandler.for("lastName")
							.attachToTextInput()}
						isDisabled={editRestrictions?.lastName.isDisabled}
					/>
					<ValidationError
						{...formHandler.for("lastName")
							.attachToValidationError()}
					/>
				</FormItem>

				<FormItem label={localize("_Pronouns")}>
					<Select
						{...formHandler.for("pronouns").attachToSelect()}
						options={pronounsOptions}
						menuTitle={localize("_Pronouns")}
					/>

					<ValidationError
						{...formHandler.for("pronouns").attachToValidationError()}
					/>
				</FormItem>

				<FormItem
					label={localize("_JobTitle")}
				>
					<TextInput
						{...formHandler.for("jobTitle")
							.attachToTextInput()}
						isDisabled={editRestrictions?.job.isDisabled}
					/>
					<ValidationError
						{...formHandler.for("jobTitle")
							.attachToValidationError()}
					/>
				</FormItem>
				<FormItem
					label={localize("_Department")}
				>
					<TextInput
						{...formHandler.for("department")
							.attachToTextInput()}
						isDisabled={editRestrictions?.department.isDisabled}
					/>
					<ValidationError
						{...formHandler.for("department")
							.attachToValidationError()}
					/>
				</FormItem>
				<FormItem
					label={localize("_OfficeLocation")}
				>
					<TextInput
						{...formHandler.for("officeLocation").attachToTextInput()}
						isDisabled={editRestrictions?.officeLocation
							.isDisabled
						}
					/>

					<ValidationError {...formHandler.for("officeLocation").attachToValidationError()} />
				</FormItem>
				<FormItem
					label={localize("_Email")}
				>
					<TextInput
						value={emailAddress ?? ""}
						type="email"
						isDisabled
					/>
				</FormItem>
				<FormItem
					label={localize("_SecondaryEmail")}
				>
					<TextInput
						{...formHandler.for("secondaryEmailAddress")
							.attachToTextInput()}
						type="email"
						isDisabled={editRestrictions?.secondaryEmail.isDisabled}
					/>
					<ValidationError
						{...formHandler.for("secondaryEmailAddress")
							.attachToValidationError()}
					/>
				</FormItem>
				<FormItem
					label={localize("_PhoneNumber")}
				>
					<TextInput
						{...formHandler.for("phoneNumber")
							.attachToTextInput()}
						type="tel"
						isDisabled={editRestrictions?.phone.isDisabled}
					/>
					<ValidationError
						{...formHandler.for("phoneNumber")
							.attachToValidationError()}
					/>
				</FormItem>
				<FormItem label={localize("_Birthday")}>
					<InputWithMoreInfo
						onMoreInfo={birthdayMoreInfoHandler}
						trackingId="birthday-more-info"
					>
						<DatePicker
							{...formHandler.for("birthday")
								.attachToDatePicker()
							}
							mode="date"
							timezone={birthdayTimezone}
						/>
					</InputWithMoreInfo>
					<ValidationError
						{...formHandler
							.for("birthday")
							.attachToValidationError()
						}
					/>
				</FormItem>
				<CustomString
					attachable={formHandler.for("customString1")}
					isDisabled={editRestrictions?.customString1.isDisabled}
				/>
				<CustomString
					attachable={formHandler.for("customString2")}
					isDisabled={editRestrictions?.customString2.isDisabled}
				/>
				<CustomString
					attachable={formHandler.for("customString3")}
					isDisabled={editRestrictions?.customString3.isDisabled}
				/>
				<CustomString
					attachable={formHandler.for("customString4")}
					isDisabled={editRestrictions?.customString4.isDisabled}
				/>
				<CustomString
					attachable={formHandler.for("customString5")}
					isDisabled={editRestrictions?.customString5.isDisabled}
				/>
				<h3>{localize("_AdditionalInformation")}</h3>
				<FormItem
					label={localize("_AboutMe")}
				>
					<TextArea
						{...formHandler.for("aboutMe")
							.attachToTextArea()
						}
						rows={6}
					/>
					<ValidationError
						{...formHandler.for("aboutMe")
							.attachToValidationError()
						}
					/>
				</FormItem>

				<h4>{localize("_TimeZone")}</h4>
				<FormItem hasNoMargin>
					<div className={styles.toggleWithMessage}>
						<div>
							<Toggle
								{...formHandler.for("hasTimeZoneAutomaticallySet")
									.attachToCheckbox()
								}
							/>
						</div>
						<span>
							{localize("_SetTimeZoneAutomatically")}
						</span>
					</div>
				</FormItem>

				<FormItem>
					<TimeZonePicker
						{...formHandler.for("timeZone")
							.attachToSelect()
						}
						placeholder={localize("_SelectTimeZone")}
						menuTitle={localize("_TimeZone")}
						isDisabled={formHandler.for("hasTimeZoneAutomaticallySet").value}
					/>
					<ValidationError
						{...formHandler.for("timeZone")
							.attachToValidationError()
						}
					/>
				</FormItem>

				<FormItem
					label={localize("_WorkingDays")}
					isForGroup
				>
					<WorkingDaysInput
						attachable={formHandler.for("workingDays")}
					/>
					<ValidationError
						{...formHandler.for("workingDays")
							.attachToValidationError()
						}
					/>
				</FormItem>
				<UserTagSelect
					label={localize("_Projects")}
					attachable={formHandler.for("projects")}
					kind={TagKind.UserProjects}
				/>
				<UserTagSelect
					label={localize("_SkillsAndExpertise")}
					attachable={formHandler.for("skills")}
					kind={TagKind.UserSkillsExp}
				/>
				<UserTagSelect
					label={localize("_SchoolsAndEducation")}
					attachable={formHandler.for("education")}
					kind={TagKind.UserEducation}
				/>
				<UserTagSelect
					label={localize("_InterestsAndHobbies")}
					attachable={formHandler.for("hobbies")}
					kind={TagKind.UserHobbies}
				/>
			</Form>
			{alertDialog}
		</>
	);
};

UserProfileForm.displayName = "UserProfileForm";

type CustomStringProps = {
	attachable: IValueInputAttachable<OptionalModel<CustomStringFormData>>;
	isDisabled?: boolean;
};

const CustomString = (props: CustomStringProps): ReactElement => {
	const {
		attachable,
		isDisabled
	} = props;

	return (
		<>
			{attachable.whenType(
				x => x.isSet,
				handler => {
					const valueHandler: IValueInputAttachable<CustomStringFormData> = handler.for("value");
					const { displayName } = valueHandler
						.reducer
						.value;

					return (
						<FormItem label={displayName}>
							<TextInput
								{...valueHandler.for("value").attachToTextInput()}
								isDisabled={isDisabled}
							/>
							<ValidationError {...valueHandler.for("value").attachToValidationError()} />
						</FormItem>
					);
				}
			)}
		</>
	);
};
CustomString.displayName = "CustomString";

const pronounsToDisplayTextMap: Record<Exclude<UserPronounKind, UserPronounKind.None>, string> = {
	[UserPronounKind.SheHer]: localize("_SheHer"),
	[UserPronounKind.HeHim]: localize("_HeHim"),
	[UserPronounKind.TheyThem]: localize("_TheyThem"),
};

export function getIdentifiesAs(pronouns: UserPronounKind): string | null {
	if (pronouns === UserPronounKind.None) {
		return null;
	}

	return pronounsToDisplayTextMap[pronouns];
}

export const pronounsOptions: readonly SelectItem<UserPronounKind>[] = [
	{
		value: UserPronounKind.None,
		text: localize("_NotSelected")
	},
	{
		value: UserPronounKind.SheHer,
		text: pronounsToDisplayTextMap[UserPronounKind.SheHer]
	},
	{
		value: UserPronounKind.HeHim,
		text: pronounsToDisplayTextMap[UserPronounKind.HeHim]
	},
	{
		value: UserPronounKind.TheyThem,
		text: pronounsToDisplayTextMap[UserPronounKind.TheyThem]
	}
];
